import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AddressAutocomplete from '../components/AddressAutocomplete';
import AddressRegionFields from '../components/AddressRegionFields';
import { saveErrorText } from '../constants/addressRegions';
import { CoachProfileShell } from '../components/ProfileShell';

export default function CoachProfilePage() {
  const { user, API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [profile, setProfile] = useState({
    cell_phone: '',
    home_address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    personal_statement: '',
    contact_via_email: false,
    contact_via_phone: false,
  });

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/coaches/profile`, getAxiosConfig());
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('No existing profile or error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const saveField = async (name, value) => {
    try {
      await axios.post(
        `${API_URL}/coaches/profile`,
        { [name]: value },
        getAxiosConfig()
      );
      setMessage('Saved');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setMessage(saveErrorText(error));
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const saveFields = async (patch) => {
    try {
      await axios.post(`${API_URL}/coaches/profile`, patch, getAxiosConfig());
      setMessage('Saved');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setMessage(saveErrorText(error));
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    }
  };

  // A pick from the address autocomplete: country is saved only when Places
  // returned one, so a legacy NULL country is never overwritten by ''.
  const applyAddressSelection = (addressData) => {
    const { country, ...rest } = addressData;
    const patch = country ? addressData : rest;
    setProfile(prev => ({ ...prev, ...patch }));
    saveFields(patch);
  };

  // Full triple on every edit; only a country switch writes country (and the
  // cleared state/postal with it).
  const handleRegionChange = (v, meta) => {
    if (meta && meta.countryChanged) {
      setProfile(prev => ({ ...prev, ...v }));
      saveFields({ country: v.country, state: v.state, zip_code: v.zip_code });
    } else {
      setProfile(prev => ({ ...prev, state: v.state, zip_code: v.zip_code }));
    }
  };

  const handleBlur = async (e) => {
    const { name } = e.target;
    await saveField(name, profile[name]);
  };

  const saveFlag = (name, checked) => {
    setProfile(prev => ({ ...prev, [name]: checked }));
    saveField(name, checked);   // POST { [name]: checked }
  };

  return (
    <CoachProfileShell activeTab="Info">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>
      )}

      {/* Contact Information */}
      <div className="settings-section">
        <h2 className="settings-section__title">Contact Information</h2>
        <p className="settings-section__desc">Your contact details.</p>
        <div className="form-field">
          <label className="form-field__label">Email</label>
          <div className="form-field__input" style={{ background: '#f5f5f5' }}>{user?.email || '—'}</div>
          <label className="cn-check" style={{ marginTop: 6 }}>
            <input type="checkbox" checked={!!profile.contact_via_email}
              onChange={e => saveFlag('contact_via_email', e.target.checked)} />
            {' '}OK to contact me here
          </label>
        </div>
        <div className="form-field">
          <label className="form-field__label" htmlFor="home_address">Home Address</label>
          <AddressAutocomplete
            id="home_address"
            name="home_address"
            value={profile.home_address}
            className="form-field__input"
            placeholder="Start typing an address..."
            onManualChange={handleChange}
            onBlur={handleBlur}
            onSelect={applyAddressSelection}
          />
        </div>
        <div className="form-field">
          <label className="form-field__label" htmlFor="address_line_2">Apt / Suite / Unit</label>
          <input
            id="address_line_2"
            type="text"
            name="address_line_2"
            value={profile.address_line_2}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-field__input"
            placeholder="Apt 4B, Suite 200, etc."
          />
        </div>
        <div className="form-field">
          <label className="form-field__label" htmlFor="city">City</label>
          <input
            id="city"
            type="text"
            name="city"
            value={profile.city}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-field__input"
          />
        </div>
        <AddressRegionFields
          country={profile.country}
          state={profile.state}
          postal={profile.zip_code}
          onChange={handleRegionChange}
          onBlur={(name) => saveField(name, profile[name])}
        />
        <div className="form-field">
          <label className="form-field__label" htmlFor="cell_phone">Cell Phone</label>
          <input
            id="cell_phone"
            type="tel"
            name="cell_phone"
            value={profile.cell_phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-field__input"
            placeholder="(555) 123-4567"
          />
          <label className="cn-check" style={{ marginTop: 6 }}>
            <input type="checkbox" checked={!!profile.contact_via_phone}
              onChange={e => saveFlag('contact_via_phone', e.target.checked)} />
            {' '}OK to contact me here
          </label>
        </div>
      </div>

    </CoachProfileShell>
  );
}
